import type { jsPDF } from 'jspdf';
import type { CtInterviewAnswerKey, CtInterviewAnswerView, CtInterviewSubmissionDetail } from '../api/types';
import {
  colors,
  contentBottom,
  contentWidth,
  drawFooters,
  drawPill,
  drawRule,
  drawSectionBar,
  filenameSlug,
  measure,
  page,
  PdfCursor,
  pillHeight,
  type Rgb,
  setFill,
  setStroke,
  setText,
  writeParagraph,
} from '../pdf/pdfKit';

/**
 * Renders one candidate's assessment as a PDF that looks like the page it was exported from —
 * filled green section bars, bordered cards, the green question badge, the answer panel tinted by
 * how the answer was marked, and the amber "Not answered" pill.
 *
 * It renders two documents, differing only in whether the answer key goes with them — see
 * {@link SubmissionPdfOptions}.
 *
 * Built in the browser rather than on the API, for one reason that decided it: this application
 * already knows that Azure App Service for Linux ships almost no fonts — it is why the initials
 * badges need a TrueType file checked into the repository (see `Assets/fonts/README.md`). A
 * server-rendered PDF of eleven free-text answers would inherit exactly that problem, and a page
 * of missing glyphs is a worse failure than a blank avatar. The browser has fonts, and jsPDF's
 * standard faces are supplied by the PDF viewer, so neither side has to ship one.
 *
 * jsPDF is loaded through a dynamic import at the call site, so it is a separate chunk that only
 * downloads when someone actually exports — the dashboard itself does not pay for it.
 *
 * It sits here, in neither half of the section, because it was written when both halves rendered
 * the same document. Only the review side calls it now (see `docs/architecture.md`), and it stays
 * outside `public/` and outside `sections/` regardless: it is a drawing routine that reaches no API
 * and holds no route, so there is nothing in its location for the public half to reach through.
 *
 * The page geometry, colours, cursor and character folding are in `../pdf/pdfKit`, shared with the
 * observation agreement's export. What is here is the shape of an assessment specifically.
 */

const style = {
  card: { paddingX: 12.5, paddingY: 11, radius: 4, border: 0.7, gapAfter: 10 },
  question: { size: 10.5, lineHeight: 15, gapAfter: 8.5 },
  badge: { size: 8.5, paddingX: 6.5, paddingY: 1.5, gapAfter: 7 },
  panel: { paddingX: 11, paddingY: 8.5, radius: 4, bar: 2.5, size: 10.5, lineHeight: 17 },
  flag: { size: 9, paddingX: 8.5, paddingY: 3.5 },

  /** The Correct / Incorrect pill at the right-hand end of the question row. */
  mark: { size: 8.5, paddingX: 7, paddingY: 1.5, gapBefore: 8 },

  /** The answer key printed under a question that did not score. */
  key: {
    paddingX: 12,
    paddingY: 9,
    radius: 4,
    gapBefore: 8,
    label: { size: 7.5, lineHeight: 10, gapAfter: 5 },
    answer: { size: 10, lineHeight: 14 },
    why: { size: 8.5, lineHeight: 12, gapBefore: 5 },
  },
} as const;

/**
 * How a marked answer did, as the pill at the end of the question row. Null when there is nothing
 * to mark — a written question, or a multiple-choice one left blank, which the card already flags.
 */
function markFor(answer: CtInterviewAnswerView): { text: string; correct: boolean } | null {
  if (answer.isCorrect === null) return null;

  return answer.isCorrect ? { text: 'Correct', correct: true } : { text: 'Incorrect', correct: false };
}

/**
 * What goes in the answer panel: for a choice, the option letter in front of the option text, the
 * way the packet prints it. A panel reading only "80%" is an answer nobody can check without the
 * question in front of them; one reading only "C" is an answer nobody can read at all.
 */
function panelTextFor(answer: CtInterviewAnswerView): string {
  const text = answer.answerText ?? '';

  return answer.selectedOptionId === null ? text : `${answer.selectedOptionId})  ${text}`;
}

/**
 * Splits an answer into display lines, preserving the candidate's own line breaks.
 *
 * An empty string survives as an empty line rather than being dropped, because a blank line
 * between paragraphs is something the candidate typed on purpose.
 */
function answerLines(doc: jsPDF, text: string, width: number): string[] {
  return text
    .split('\n')
    .flatMap((line) => (line.trim().length === 0 ? [''] : measure(doc, line, width, style.panel.size, false)));
}

/**
 * One question, its answer, and — where the question did not score — the answer key under it.
 *
 * Two blocks rather than one, because the card fragments across pages and the key does not: a key
 * is three short lines that belong together, and splitting them to save a few points at the foot of
 * a page is a worse read than starting them on the next one.
 */
function drawAnswerCard(
  cursor: PdfCursor,
  answer: CtInterviewAnswerView,
  number: number | null,
  includeKey: boolean,
  label?: string,
): void {
  drawCard(cursor, answer, number, label);

  // The same rule the screen follows: a correct answer needs no key printed under it. The whole
  // block goes when this is the copy without the key — which is the only difference between the two
  // exports, marking included.
  const key = includeKey && answer.isCorrect !== true ? answer.key : null;
  if (key !== null) {
    drawAnswerKey(cursor, key);
  }

  cursor.y += style.card.gapAfter;
}

/**
 * The question-and-answer card itself, drawn to match `.interview-answer` on screen.
 *
 * The card's chrome has to be painted before the text that sits on it, so everything is measured
 * first and only then drawn — the opposite of the flowing paragraphs elsewhere in this file.
 *
 * An answer can be longer than a page (the field accepts 4000 characters), so the card fragments:
 * each page draws its own bordered card around the slice of answer that fits, with the question on
 * the first and a "continued" marker on the rest. One loop covers both that case and the ordinary
 * one-fragment card.
 */
function drawCard(cursor: PdfCursor, answer: CtInterviewAnswerView, number: number | null, label?: string): void {
  const { doc } = cursor;
  const blank = answer.answerText === null;

  const innerWidth = contentWidth - style.card.paddingX * 2;

  // The badge sits on the first line and the question text hangs beside it, as the flex row does.
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(style.badge.size);
  const badgeText = number === null ? null : `Q${number}`;
  const badgeWidth = badgeText === null ? 0 : doc.getTextWidth(badgeText) + style.badge.paddingX * 2;
  const questionIndent = badgeText === null ? 0 : badgeWidth + style.badge.gapAfter;

  // The mark takes its width out of the question's, the way the flex row on screen gives way to it.
  const mark = markFor(answer);
  doc.setFontSize(style.mark.size);
  const markWidth = mark === null ? 0 : doc.getTextWidth(mark.text) + style.mark.paddingX * 2;
  const markGutter = mark === null ? 0 : markWidth + style.mark.gapBefore;

  const questionLines = measure(
    doc,
    label ?? answer.questionPrompt,
    innerWidth - questionIndent - markGutter,
    style.question.size,
    true,
  );
  const questionHeight = Math.max(
    questionLines.length * style.question.lineHeight,
    pillHeight(style.badge.size, style.badge.paddingY),
    mark === null ? 0 : pillHeight(style.mark.size, style.mark.paddingY),
  );

  const panelWidth = innerWidth;
  const panelTextWidth = panelWidth - style.panel.bar - style.panel.paddingX * 2;
  const lines = blank ? [] : answerLines(doc, panelTextFor(answer), panelTextWidth);

  const flagH = pillHeight(style.flag.size, style.flag.paddingY);
  const headHeight = style.card.paddingY + questionHeight + style.question.gapAfter;

  // The blank card has no panel to fragment, so it is the simple case: one box, always whole.
  if (blank) {
    const height = headHeight + flagH + style.card.paddingY;
    cursor.reserve(height);
    drawCardBox(doc, cursor.y, height, true);
    drawCardHead(doc, cursor.y, badgeText, questionIndent, questionLines, true, mark, markWidth);

    drawPill(doc, page.margin + style.card.paddingX, cursor.y + headHeight, 'Not answered', {
      size: style.flag.size,
      paddingX: style.flag.paddingX,
      paddingY: style.flag.paddingY,
      color: colors.warn,
      background: colors.warnBackground,
      border: colors.warnBorder,
    });

    cursor.y += height;
    return;
  }

  const wholeHeight =
    headHeight + style.panel.paddingY * 2 + lines.length * style.panel.lineHeight + style.card.paddingY;

  // Start it on a fresh page rather than splitting it, when splitting is avoidable.
  if (wholeHeight > cursor.available && wholeHeight <= contentBottom - page.margin) {
    cursor.newPage();
  }

  let remaining = lines;
  let first = true;

  while (true) {
    const head = first ? headHeight : style.card.paddingY + style.question.lineHeight;
    const overhead = head + style.panel.paddingY * 2 + style.card.paddingY;
    const roomForOneLine = overhead + style.panel.lineHeight;

    if (cursor.available < roomForOneLine) {
      cursor.newPage();
      continue;
    }

    const fits = Math.floor((cursor.available - overhead) / style.panel.lineHeight);
    const take = Math.min(remaining.length, Math.max(1, fits));
    const slice = remaining.slice(0, take);

    const panelHeight = style.panel.paddingY * 2 + slice.length * style.panel.lineHeight;
    const height = head + panelHeight + style.card.paddingY;

    drawCardBox(doc, cursor.y, height, false);

    if (first) {
      drawCardHead(doc, cursor.y, badgeText, questionIndent, questionLines, false, mark, markWidth);
    } else {
      setText(doc, colors.muted);
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(8.5);
      doc.text('continued', page.margin + style.card.paddingX, cursor.y + style.card.paddingY + 8.5);
    }

    drawPanel(doc, cursor.y + head, panelWidth, panelHeight, slice, panelTone(answer));

    cursor.y += height;
    remaining = remaining.slice(take);
    first = false;

    if (remaining.length === 0) {
      return;
    }

    cursor.newPage();
  }
}

/** The card's own border and fill — solid for an answered question, dashed for a blank one. */
function drawCardBox(doc: jsPDF, y: number, height: number, blank: boolean): void {
  setStroke(doc, colors.hairline);
  doc.setLineWidth(style.card.border);

  if (blank) {
    doc.setLineDashPattern([2.5, 2.5], 0);
    // Transparent on screen; the page under it is white either way, so stroke only.
    doc.roundedRect(page.margin, y, contentWidth, height, style.card.radius, style.card.radius, 'S');
    doc.setLineDashPattern([], 0);
    return;
  }

  setFill(doc, colors.surface);
  doc.roundedRect(page.margin, y, contentWidth, height, style.card.radius, style.card.radius, 'FD');
}

/** The question badge, the question text beside it, and the Correct / Incorrect mark at the end. */
function drawCardHead(
  doc: jsPDF,
  y: number,
  badgeText: string | null,
  indent: number,
  lines: string[],
  blank: boolean,
  mark: { text: string; correct: boolean } | null,
  markWidth: number,
): void {
  const top = y + style.card.paddingY;

  if (mark !== null) {
    // Right-aligned against the card's inner edge, which is the width the question was wrapped to
    // leave free.
    drawPill(doc, page.margin + contentWidth - style.card.paddingX - markWidth, top, mark.text, {
      size: style.mark.size,
      paddingX: style.mark.paddingX,
      paddingY: style.mark.paddingY,
      color: mark.correct ? colors.greenDark : colors.danger,
      background: mark.correct ? colors.mint : colors.dangerBackground,
      border: mark.correct ? colors.greenLight : colors.dangerBorder,
    });
  }

  if (badgeText !== null) {
    // On a blank card the badge drops to the outline treatment, so a column of solid green badges
    // beside empty cards cannot read as though they were answered.
    drawPill(doc, page.margin + style.card.paddingX, top, badgeText, {
      size: style.badge.size,
      paddingX: style.badge.paddingX,
      paddingY: style.badge.paddingY,
      color: blank ? colors.muted : colors.surface,
      background: blank ? colors.surface : colors.green,
      border: blank ? colors.hairline : undefined,
    });
  }

  setText(doc, colors.ink);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(style.question.size);

  lines.forEach((line, index) => {
    doc.text(
      line,
      page.margin + style.card.paddingX + indent,
      top + style.question.size + index * style.question.lineHeight,
    );
  });

}

/**
 * The answer key under a question that did not score — the dashed panel matching `.interview-key`
 * on screen, and the reason this export is an internal document.
 *
 * Deliberately not the mint-and-green of the answer panel: that treatment means "this is what the
 * candidate wrote", and this is the opposite of that, so it reads as the reviewer's own margin note
 * rather than as a second answer.
 */
function drawAnswerKey(cursor: PdfCursor, key: CtInterviewAnswerKey): void {
  const { doc } = cursor;
  const innerWidth = contentWidth - style.key.paddingX * 2;

  const optionLines = measure(
    doc,
    `${key.correctOptionId})  ${key.correctOptionText}`,
    innerWidth,
    style.key.answer.size,
    true,
  );
  const whyLines = measure(doc, key.why, innerWidth, style.key.why.size, false);

  const height =
    style.key.paddingY * 2 +
    style.key.label.lineHeight +
    style.key.label.gapAfter +
    optionLines.length * style.key.answer.lineHeight +
    style.key.why.gapBefore +
    whyLines.length * style.key.why.lineHeight;

  cursor.y += style.key.gapBefore;
  cursor.reserve(height);

  setFill(doc, colors.canvas);
  setStroke(doc, colors.hairline);
  doc.setLineWidth(style.card.border);
  doc.setLineDashPattern([2.5, 2.5], 0);
  doc.roundedRect(page.margin, cursor.y, contentWidth, height, style.key.radius, style.key.radius, 'FD');
  doc.setLineDashPattern([], 0);

  const x = page.margin + style.key.paddingX;
  let y = cursor.y + style.key.paddingY;

  setText(doc, colors.muted);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(style.key.label.size);
  doc.text('CORRECT ANSWER', x, y + style.key.label.size);
  y += style.key.label.lineHeight + style.key.label.gapAfter;

  setText(doc, colors.ink);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(style.key.answer.size);
  optionLines.forEach((line, index) =>
    doc.text(line, x, y + style.key.answer.size + index * style.key.answer.lineHeight),
  );
  y += optionLines.length * style.key.answer.lineHeight + style.key.why.gapBefore;

  setText(doc, colors.body);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(style.key.why.size);
  whyLines.forEach((line, index) => doc.text(line, x, y + style.key.why.size + index * style.key.why.lineHeight));

  cursor.y += height;
}

/**
 * How the answer panel is tinted, which on this page is the same question as how the answer did.
 *
 * Neutral where there is nothing to be right about — the written section, and anything recorded
 * before the assessment had correct answers at all. The panel is the biggest thing on the card, so
 * it is what the verdict is read off; a wrong answer printed in the same green as a right one is a
 * false lead that a small "Incorrect" pill above it does not win against.
 */
function panelTone(answer: CtInterviewAnswerView): { bar: Rgb; background: Rgb } {
  if (answer.isCorrect === null) return { bar: colors.hairline, background: colors.shade };

  return answer.isCorrect
    ? { bar: colors.green, background: colors.mint }
    : { bar: colors.danger, background: colors.dangerBackground };
}

/** The answer panel with its accent down the left edge, tinted by {@link panelTone}. */
function drawPanel(
  doc: jsPDF,
  y: number,
  width: number,
  height: number,
  lines: string[],
  tone: { bar: Rgb; background: Rgb },
): void {
  const x = page.margin + style.card.paddingX;

  setFill(doc, tone.background);
  doc.roundedRect(x, y, width, height, style.panel.radius, style.panel.radius, 'F');

  // Square, not rounded: on screen the panel's left corners are flat and the accent runs the full
  // height of the block.
  setFill(doc, tone.bar);
  doc.rect(x, y, style.panel.bar, height, 'F');

  setText(doc, colors.ink);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(style.panel.size);

  lines.forEach((line, index) => {
    if (line.length === 0) return;

    doc.text(
      line,
      x + style.panel.bar + style.panel.paddingX,
      y + style.panel.paddingY + style.panel.size + index * style.panel.lineHeight,
    );
  });
}

/**
 * Which of the two documents this is.
 *
 * Both carry the candidate's answers and how each one was marked. Only the key version carries the
 * answer key — the sheet that stays true for every future candidate, which is the part worth being
 * deliberate about passing on. A score is about one person and is on the first line of either copy,
 * so withholding the marks from the plain one would cost it its usefulness without buying any
 * discretion.
 */
export interface SubmissionPdfOptions {
  includeKey: boolean;
}

/** Draws the whole document. Separate from saving it so it can be exercised outside a browser. */
export function renderSubmissionPdf(
  doc: jsPDF,
  submission: CtInterviewSubmissionDetail,
  options: SubmissionPdfOptions,
): void {
  const cursor = new PdfCursor(doc);
  const submitted = new Date(submission.submittedAtUtc);
  const unanswered = submission.questionCount - submission.answeredCount;
  const { includeKey } = options;

  writeParagraph(cursor, 'TVG-MEDULLA LLC', { size: 8, lineHeight: 11, bold: true, color: colors.muted });
  cursor.y += 2;

  // Written here rather than taken from the submission, which does not carry the form's title. It
  // has to be kept in step with CtInterviewForm.Title by hand — the one string in this export that
  // can drift, and the reason it is named right under the organisation it belongs to.
  writeParagraph(cursor, 'Patient Care Specialist (CT) Candidate Skills Assessment', {
    size: 16,
    lineHeight: 20,
    bold: true,
    color: colors.ink,
  });

  // Said at the top of the key version, and again in its footer on every page — see the end of this
  // function. The pages below carry the answer key beside every question that was missed, which is
  // the sheet the paper packet heads "DO NOT SHARE WITH CANDIDATE", and a document that only admits
  // that on page one is a document someone will print page three of.
  if (includeKey) {
    cursor.y += 6;
    writeParagraph(cursor, 'INTERNAL USE ONLY — includes the answer key', {
      size: 8,
      lineHeight: 11,
      bold: true,
      color: colors.danger,
    });
  }

  cursor.y += 10;
  drawRule(cursor);
  cursor.y += 12;

  writeParagraph(cursor, submission.candidateName, { size: 13, lineHeight: 17, bold: true, color: colors.ink });
  cursor.y += 2;
  writeParagraph(
    cursor,
    `${submission.location}  ·  ${submitted.toLocaleDateString()} ${submitted.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}  ·  ` +
      `${submission.answeredCount} of ${submission.questionCount} answered` +
      // Out of the marked questions, not out of all of them — the written section is read, not
      // scored. Left off entirely for an assessment sat before the form had anything to mark, where
      // "0 of 0 correct" would read as a candidate who got everything wrong.
      (submission.scoredCount > 0 ? `  ·  ${submission.correctCount} of ${submission.scoredCount} correct` : ''),
    { size: 9, lineHeight: 12, color: colors.muted },
  );

  // The same amber tag the detail page's header carries.
  if (unanswered > 0) {
    cursor.y += 6;
    drawPill(doc, page.margin, cursor.y, `${unanswered} left blank`, {
      size: style.flag.size,
      paddingX: style.flag.paddingX,
      paddingY: style.flag.paddingY,
      color: colors.warn,
      background: colors.warnBackground,
      border: colors.warnBorder,
    });
    cursor.y += pillHeight(style.flag.size, style.flag.paddingY);
  }

  cursor.y += 16;

  for (const section of submission.sections) {
    drawSectionBar(cursor, section.title);
    section.answers.forEach((answer, index) => drawAnswerCard(cursor, answer, index + 1, includeKey));
    cursor.y += 6;
  }

  if (submission.retiredAnswers.length > 0) {
    drawSectionBar(cursor, 'No longer asked');
    writeParagraph(cursor, 'The questionnaire changed while this candidate was answering.', {
      size: 9,
      lineHeight: 12,
      color: colors.muted,
    });
    cursor.y += 8;

    for (const answer of submission.retiredAnswers) {
      drawAnswerCard(cursor, answer, null, includeKey, answer.questionId);
    }
  }

  // The footer runs on every page, so the key version says what it is on every page rather than only
  // on the one someone happens to have in front of them.
  drawFooters(
    doc,
    includeKey
      ? `${submission.candidateName}  ·  Internal — includes the answer key`
      : submission.candidateName,
  );
}

/**
 * A filename someone can find again in a downloads folder: who, and when they sat it. The date is
 * ISO so a folder of these sorts chronologically rather than by whatever the locale put first.
 *
 * The key version says so in the name, because the two are otherwise indistinguishable in a
 * downloads folder and only one of them is safe to attach to anything.
 */
export function submissionPdfFilename(
  submission: CtInterviewSubmissionDetail,
  options: SubmissionPdfOptions,
): string {
  const slug = filenameSlug(submission.candidateName);
  const date = submission.submittedAtUtc.slice(0, 10);
  const suffix = options.includeKey ? '-answer-key' : '';

  return `ct-assessment-${slug || 'candidate'}-${date}${suffix}.pdf`;
}

/** Builds the PDF and hands it to the browser as a download. */
export async function exportSubmissionPdf(
  submission: CtInterviewSubmissionDetail,
  options: SubmissionPdfOptions,
): Promise<void> {
  const { jsPDF: JsPdf } = await import('jspdf');

  const doc = new JsPdf({ unit: 'pt', format: 'a4', compress: true });
  renderSubmissionPdf(doc, submission, options);
  doc.save(submissionPdfFilename(submission, options));
}
